import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, PointElement, LineElement,
  Filler, Tooltip, Legend,
} from 'chart.js';
import ChartCard from '../components/chart/ChartCard';
import { useDashboardData } from '../data/DashboardDataProvider';
import { setOptions, setOptionsBarMultiColored, chartConfigMultiLinedStackedBar } from '../utils/chartConfigs';
import { colors } from '../utils/colors';
import {
  getDataSet, getSumDataSet, getFilteredData, getFilteredLabels,
  setDatesAsWeeks, setDatesAsMonths, getDataSetGrouped, groupBy, getTotal,
} from '../utils/dataManager';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Filler, Tooltip, Legend);

const STACKED = true;
const FILL = true;

const AnalysisDetailsPage = () => {
  const { analyticsData, errors } = useDashboardData();
  const { analyticsEventsPerDay } = analyticsData;

  const getDataFilteredAndGrouped = (filter, group) => {
    const analyticsEventsFiltered = getFilteredData(analyticsEventsPerDay, filter);
    let analyticsEventsGrouped = [];
    switch (group) {
      case 'week':
        analyticsEventsGrouped = groupBy(setDatesAsWeeks(analyticsEventsFiltered), 'creation_date');
        break;
      case 'month':
        analyticsEventsGrouped = groupBy(setDatesAsMonths(analyticsEventsFiltered), 'creation_date');
        break;
      default:
        break;
    }

    const getDataSetFrom = (getter, source) => ({
      analyticsEventsLabels: group ? Object.keys(analyticsEventsGrouped) : getFilteredLabels(analyticsEventsFiltered),
      recordingStart: getter(source, 'recording_start'),
      audioImport: getter(source, 'audio_import'),
      videoImport: getter(source, 'video_import'),
      songDisplay: getter(source, 'song_display'),
      recordingPlusImport: getSumDataSet([getter(source, 'recording_start'), getter(source, 'audio_import'), getter(source, 'video_import')]),
      analysisBegin: getter(source, 'cloud_analysis_begin'),
      analysisUploadFinish: getter(source, 'cloud_analysis_upload_finish'),
      analysisEnd: getter(source, 'cloud_analysis_end'),
      analysisAborted: getter(source, 'analysis_aborted'),
      snippetReady: getter(source, 'snippet_ready'),
      songReady: getter(source, 'song_ready'),
      chordAnalysisReady: getter(source, 'chord_analysis_ready'),
      recordingStopTooShort: getter(source, 'recording_stop_too_short'),
      analysisResultMissing: getter(source, 'analysis_result_missing'),
      analysisAbortedCloudAnalysisBegin: getter(source, 'analysis_aborted_cloud_analysis_begin'),
      analysisAbortedCloudUploadFinish: getter(source, 'analysis_aborted_cloud_analysis_upload_finish'),
      analysisAbortedCloudAnalysisEnd: getter(source, 'analysis_aborted_cloud_analysis_end'),
      analysisAbortedSnippetReady: getter(source, 'analysis_aborted_snippet_ready'),
      analysisAbortedSongReady: getter(source, 'analysis_aborted_song_ready'),
      analysisAbortedChordAnalysisReady: getter(source, 'analysis_aborted_chord_analysis_ready'),
    });

    if (group) return getDataSetFrom(getDataSetGrouped, analyticsEventsGrouped);
    return getDataSetFrom(getDataSet, analyticsEventsFiltered);
  };

  const analysisSteps = (data) => {
    const labels = ['import+rec', 'cloud begin', 'upload finish', 'cloud end', 'snippet ready', 'song ready', 'chord ready', 'song display', 'manual abort'];
    const stepsValues = {
      recordingPlusImport: getTotal(data.recordingPlusImport),
      analysisBegin: getTotal(data.analysisBegin),
      analysisUploadFinish: getTotal(data.analysisUploadFinish),
      analysisEnd: getTotal(data.analysisEnd),
      snippetReady: getTotal(data.snippetReady),
      songReady: getTotal(data.songReady),
      chordAnalysisReady: getTotal(data.chordAnalysisReady),
      songDisplay: getTotal(data.songDisplay),
      manualAbort: 0,
    };
    const abortedValues = {
      dummyFirst: 0,
      dummyRecTooShort: 0,
      analysisAbortedCloudAnalysisBegin: getTotal(data.analysisAbortedCloudAnalysisBegin),
      analysisAbortedCloudUploadFinish: getTotal(data.analysisAbortedCloudUploadFinish),
      analysisAbortedCloudAnalysisEnd: getTotal(data.analysisAbortedCloudAnalysisEnd),
      analysisAbortedSnippetReady: getTotal(data.analysisAbortedSnippetReady),
      analysisAbortedSongReady: getTotal(data.analysisAbortedSongReady),
      analysisAbortedChordAnalysisReady: getTotal(data.analysisAbortedChordAnalysisReady),
      analysisAborted: getTotal(data.analysisAborted),
    };
    const analysisResultMissing = getTotal(data.analysisResultMissing);
    const recordingStopTooShort = getTotal(data.recordingStopTooShort);

    stepsValues.analysisBegin -= recordingStopTooShort;
    stepsValues.analysisUploadFinish -= recordingStopTooShort;
    stepsValues.analysisUploadFinish -= abortedValues.analysisAbortedCloudAnalysisBegin;

    const total = stepsValues.recordingPlusImport;
    const percentage = Object.values(stepsValues).map(v => Math.round(v / total * 100));
    percentage.pop();
    percentage.push(Math.round(abortedValues.analysisAborted / total * 100));

    const labelsWithPct = labels.map((l, i) => `${l} ${percentage[i]}%`);
    return { stepsValues, abortedValues, analysisResultMissing, recordingStopTooShort, labels: labelsWithPct };
  };

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
  sixMonthsAgo.setDate(sixMonthsAgo.getDate() - sixMonthsAgo.getDay() + 1);
  const daysSinceSixMonthsAgo = Math.round((today - sixMonthsAgo) / (1000 * 60 * 60 * 24));

  const dataLast6Months = getDataFilteredAndGrouped(daysSinceSixMonthsAgo, 'week');
  const dataLast7Days = getDataFilteredAndGrouped(7);
  const dataLast30Days = getDataFilteredAndGrouped(30);

  const analysisStepsLast6Months = analysisSteps(dataLast6Months);
  const analysisStepsLast7Days = analysisSteps(dataLast7Days);
  const analysisStepsLast30Days = analysisSteps(dataLast30Days);

  const failed = dataLast6Months.recordingPlusImport.map((total, i) => total - dataLast6Months.songDisplay[i] - dataLast6Months.analysisAborted[i]);
  const analysisDetails = [[dataLast6Months.audioImport, dataLast6Months.videoImport, dataLast6Months.recordingStart], [dataLast6Months.songDisplay, dataLast6Months.analysisAborted, failed]];

  const addAbortData = (data) => {
    const { stepsValues, abortedValues, analysisResultMissing, recordingStopTooShort } = data;
    const missingValues = new Array(9).fill(0);
    missingValues[3] = analysisResultMissing;
    const tooShortValues = new Array(9).fill(0);
    tooShortValues[1] = recordingStopTooShort;
    return [Object.values(stepsValues), Object.values(abortedValues), tooShortValues, missingValues];
  };

  return (
    <section>
      <div className="chart-grid">
        <div className="chart-col">
          <ChartCard header="Analysis Funnel Last 6 Months" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            hideAvg hideTotal>
            <Bar data={chartConfigMultiLinedStackedBar(
              analysisStepsLast6Months.labels,
              [['Events', 'Analysis aborted', 'Recording too short', 'Analysis missing']],
              [addAbortData(analysisStepsLast6Months)],
              [[colors.green, colors.lightred, colors.babyblue, colors.orange]],
              FILL, STACKED)}
              options={setOptionsBarMultiColored(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Analysis Started vs Result Last 6 Months (Stacked)" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            hideAvg hideTotal>
            <Bar data={chartConfigMultiLinedStackedBar(
              dataLast6Months.analyticsEventsLabels,
              [['Audio import', 'YouTube import', 'Recording'], ['Finished', 'Aborted', 'Failed']],
              analysisDetails,
              [[colors.lightblue, colors.variant_lightpurple, colors.lightred], [colors.green, colors.yellow, colors.darkred]],
              FILL, STACKED)}
              options={setOptions(STACKED, STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Analysis Steps Last 30 Days" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            hideAvg hideTotal>
            <Bar data={chartConfigMultiLinedStackedBar(
              analysisStepsLast30Days.labels,
              [['Events', 'Analysis aborted', 'Recording too short', 'Analysis missing']],
              [addAbortData(analysisStepsLast30Days)],
              [[colors.green, colors.lightred, colors.babyblue, colors.orange]],
              FILL, STACKED)}
              options={setOptionsBarMultiColored(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Analysis Steps Last 7 Days" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            hideAvg hideTotal>
            <Bar data={chartConfigMultiLinedStackedBar(
              analysisStepsLast7Days.labels,
              [['Events', 'Analysis aborted', 'Recording too short', 'Analysis missing']],
              [addAbortData(analysisStepsLast7Days)],
              [[colors.green, colors.lightred, colors.babyblue, colors.orange]],
              FILL, STACKED)}
              options={setOptionsBarMultiColored(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col-full">
          <div className="card">
            <div className="card-content">
              <ul>
                <li>Inspelningar som avbryts under inräkning får inte recording-start eller analysis-aborted, men ett recording-stop med negativt duration.</li>
                <li>Inspelningar som avbryts innan 8 sek får recording-start och recording-stop, men även cloud-analysis-begin och cloud-analysis-upload-finish. De räknas i recording_stop_too_short.</li>
                <li>Inspelningar som avbryts efter cloud-analysis-begin och cloud-analysis-upload-finish får analysis-aborted med step: cloud-analysis-begin.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AnalysisDetailsPage;
